/**
 * @file Meter-space coordinate system.
 *
 * The handball field is 40 m × 20 m. All game state (player positions, arrow points,
 * keyframes) is authored in meter-space (0..40, 0..20). This module converts to/from
 * pixel-space for rendering, letterboxing the field inside its container.
 *
 * Pure functions — no DOM, no window, no side effects. Unit-testable in node.
 */

/**
 * @typedef {object} Vec2m
 * @property {number} x meters, 0..40
 * @property {number} y meters, 0..20
 */

/**
 * @typedef {object} Vec2px
 * @property {number} px CSS pixels
 * @property {number} py CSS pixels
 */

/**
 * @typedef {object} FieldTransform
 * @property {number} pxPerMeter    CSS-px per meter (uniform — field never stretches)
 * @property {number} offsetXPx     letterbox offset X (CSS px)
 * @property {number} offsetYPx     letterbox offset Y (CSS px)
 * @property {number} fieldWidthM   40 (handball standard)
 * @property {number} fieldHeightM  20
 * @property {number} containerWidthPx
 * @property {number} containerHeightPx
 * @property {number} dpr           device pixel ratio at transform creation
 */

/** Standard IHF handball field dimensions. */
export const FIELD_WIDTH_M = 40;
export const FIELD_HEIGHT_M = 20;

/**
 * Compute the transform for a given container. Letterboxes: field keeps 2:1 aspect,
 * is centered, never stretches.
 * @param {object} args
 * @param {number} args.containerWidthPx
 * @param {number} args.containerHeightPx
 * @param {number} [args.fieldWidthM]
 * @param {number} [args.fieldHeightM]
 * @param {number} [args.dpr]          device pixel ratio (default 1)
 * @param {number} [args.paddingPx]    optional inner padding (default 16)
 * @returns {FieldTransform}
 */
export function createTransform({
  containerWidthPx,
  containerHeightPx,
  fieldWidthM = FIELD_WIDTH_M,
  fieldHeightM = FIELD_HEIGHT_M,
  dpr = 1,
  paddingPx = 16,
}) {
  if (containerWidthPx <= 0 || containerHeightPx <= 0) {
    throw new Error(
      `createTransform: container must be positive (${containerWidthPx}×${containerHeightPx})`,
    );
  }

  const usableW = Math.max(0, containerWidthPx - paddingPx * 2);
  const usableH = Math.max(0, containerHeightPx - paddingPx * 2);

  const scaleByW = usableW / fieldWidthM;
  const scaleByH = usableH / fieldHeightM;
  const pxPerMeter = Math.min(scaleByW, scaleByH);

  const fieldWPx = pxPerMeter * fieldWidthM;
  const fieldHPx = pxPerMeter * fieldHeightM;

  const offsetXPx = (containerWidthPx - fieldWPx) / 2;
  const offsetYPx = (containerHeightPx - fieldHPx) / 2;

  return {
    pxPerMeter,
    offsetXPx,
    offsetYPx,
    fieldWidthM,
    fieldHeightM,
    containerWidthPx,
    containerHeightPx,
    dpr,
  };
}

/**
 * Convert a meter-space point to CSS-pixel coordinates.
 * @param {FieldTransform} t
 * @param {Vec2m} m
 * @returns {Vec2px}
 */
export function metersToPx(t, m) {
  return {
    px: t.offsetXPx + m.x * t.pxPerMeter,
    py: t.offsetYPx + m.y * t.pxPerMeter,
  };
}

/**
 * Convert a CSS-pixel point back to meter-space (for input hit testing).
 * @param {FieldTransform} t
 * @param {Vec2px} p
 * @returns {Vec2m}
 */
export function pxToMeters(t, p) {
  return {
    x: (p.px - t.offsetXPx) / t.pxPerMeter,
    y: (p.py - t.offsetYPx) / t.pxPerMeter,
  };
}

/**
 * @param {FieldTransform} t
 * @returns {{ x: number, y: number, width: number, height: number }} CSS-px bounds
 */
export function getFieldPixelRect(t) {
  return {
    x: t.offsetXPx,
    y: t.offsetYPx,
    width: t.pxPerMeter * t.fieldWidthM,
    height: t.pxPerMeter * t.fieldHeightM,
  };
}

/**
 * Scale a meter length to CSS pixels. Useful for line widths, radii.
 * @param {FieldTransform} t
 * @param {number} meters
 * @returns {number}
 */
export function metersToPxLength(t, meters) {
  return meters * t.pxPerMeter;
}
